// Modo viagem (0.23.0, pedido do titular em 12/10/2026): as contas, puras
// (testadas em test/app/modo-viagem.test.js).
//   - O previsto da viagem: feito na hora de aceitar a carga, com as contas do
//     Raio-X (o mesmo custo; a sobra é a do Raio-X), ou pelas cargas da viagem.
//   - O que o app acompanha na estrada: a rota, os abastecimentos, os
//     horários, a manutenção, o pedágio, os documentos e os gastos.
//   - No fim, o previsto e o realizado, com as principais causas da
//     diferença (combustível, espera, pedágio…).
//   - As viagens encerradas ensinam o app: o ritmo (km por dia) e a espera
//     para carregar e descarregar de verdade entram no Planejar o trajeto e no
//     previsto das próximas viagens.
import { diasEntre } from './datas.js';
import { lerNumero } from './formato.js';
import { FATOR_DA_ESTRADA, kmEntre } from './geo.js';

export const MODO_VIAGEM = {
  kmPorDia: 600, // sem viagens com os horários, o mesmo do Planejar o trajeto
  esperaCarregar: 0.5, // dias
  esperaDescarregar: 0.5,
  reservaDoTanque: 0.15, // abastece com 15% ainda no tanque
  pertoDoPolo: 150, // km: o ponto do abastecimento ganha o nome da cidade-polo mais perto até aí
  avisoDeDocumento: 30, // dias antes de vencer, o documento entra nos avisos
  viagensParaAprender: 3, // viagens encerradas com os horários, para o app usar o ritmo delas
  esperasParaAprender: 3,
  diasParaAprender: 365,
  kmPorDiaAprendido: { min: 250, max: 1200 },
  esperaAprendida: { min: 0.05, max: 5 },
  causaMinima: 1, // R$: diferença menor que isso não entra nas causas
  diasMinimos: 0.25, // viagem mais curta que isso (6 horas): sem a conta por dia
  principais: 3,
};

const vivo = (x) => x && !x.deleted_at;
const ok = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const reais = (v) => Math.round((Number(v) || 0) * 100) / 100;
const centavos = (v) => Math.round((Number(v) || 0) * 100);
const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
const DIA_MS = 86400000;

export const lugarTexto = (cidade, uf) => [cidade, uf].filter(Boolean).join('/');

export function mediana(valores) {
  const xs = valores.filter(ok).map(Number).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const meio = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[meio] : (xs[meio - 1] + xs[meio]) / 2;
}

// O link do Raio-X para "Aceitar e iniciar a viagem" (telas/iniciar-viagem.js).
//   d: o que está no formulário do Raio-X · km: os km carregado
export function linkDeIniciar({ d, km, complementos = [] }) {
  const busca = new URLSearchParams({
    origem: d.origem, uf_origem: d.uf_origem, destino: d.destino, uf_destino: d.uf_destino,
    valor: String(lerNumero(d.valor)), km: String(km), tipo: d.tipo || 'carga_geral', eixos: String(d.eixos || 5),
    volta: d.volta === 'carregada' ? 'carregada' : 'vazia', reserva: String(lerNumero(d.reserva) ?? 0), tabela: d.tabela || 'A',
  });
  if (d.caminhao) busca.set('caminhao', d.caminhao);
  if (lerNumero(d.consumo)) busca.set('consumo', String(lerNumero(d.consumo)));
  if (lerNumero(d.diesel)) busca.set('diesel', String(lerNumero(d.diesel)));
  const comp = complementos.filter((c) => c.valor > 0).map((c) => `${c.valor}:${c.kmExtra || 0}`).join(';');
  if (comp) busca.set('comp', comp);
  return `#/viagens/iniciar?${busca.toString()}`;
}

// ---------------------------------------------------------------------
// Os eventos
// ---------------------------------------------------------------------
export const TIPOS_DE_PARADA = [
  ['descanso', 'Descanso'], ['refeicao', 'Refeição'], ['pernoite', 'Pernoite'], ['abastecimento', 'Abastecimento'],
  ['fiscalizacao', 'Fiscalização'], ['mecanico', 'Mecânico'], ['outro', 'Outro motivo'],
];

const porMomento = (a, b) => String(a.momento).localeCompare(String(b.momento))
  || String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''));

export function eventosEmOrdem(eventos) {
  return (eventos ?? []).filter(vivo).sort(porMomento);
}

const ultimo = (eventos, filtro) => eventosEmOrdem(eventos).filter(filtro).at(-1) ?? null;
export const inicioDaViagem = (eventos) => eventosEmOrdem(eventos).find((e) => e.tipo === 'inicio') ?? null;
export const fimDaViagem = (eventos) => ultimo(eventos, (e) => e.tipo === 'fim');

// O previsto que vale: o do último evento com previsto ("plano" ou "inicio").
export function planoDaViagem(eventos) {
  return ultimo(eventos, (e) => (e.tipo === 'plano' || e.tipo === 'inicio') && e.dados?.plano)?.dados.plano ?? null;
}

// Chegou e saiu (para carregar ou descarregar) de uma carga: { chegada, saida }.
// Sem a saída da descarga, vale a hora do "Carga entregue" (frete.entregue_em).
export function passagem(eventos, freteId, motivo, frete = null) {
  const doFrete = eventosEmOrdem(eventos).filter((e) => e.frete_id === freteId && e.motivo === motivo);
  let saida = doFrete.filter((e) => e.tipo === 'saida').at(-1) ?? null;
  if (!saida && motivo === 'descarregar' && frete?.entregue_em) saida = { tipo: 'saida', motivo, momento: frete.entregue_em, frete_id: freteId };
  return { chegada: doFrete.find((e) => e.tipo === 'chegada') ?? null, saida };
}

const horasEntre = (a, b) => (Date.parse(b) - Date.parse(a)) / 3600000;

// ---------------------------------------------------------------------
// O previsto
// ---------------------------------------------------------------------
// As partes das despesas da viagem por km (lib/raiox.js despesasPorKm):
// pedágio, as do dia (alimentação, pernoite, estacionamento) e as outras.
// Números de antes da 0.23.0 não têm as partes: tudo vai em "outras".
export function partesDasDespesas(custo) {
  const viagem = Number(custo?.porKm?.viagem) || 0;
  const p = custo?.despesas?.partes;
  if (!p) return { pedagio: 0, porDia: 0, outras: viagem };
  return { pedagio: Number(p.pedagio) || 0, porDia: Number(p.porDia) || 0, outras: Number(p.outras) || 0 };
}

// O ritmo e as esperas que valem: os aprendidos das viagens (com viagens
// suficientes) ou os de partida.
export function ritmoDaEmpresa(aprendido) {
  return {
    kmPorDia: aprendido?.kmPorDia ?? MODO_VIAGEM.kmPorDia,
    esperaCarregar: aprendido?.esperaCarregar ?? MODO_VIAGEM.esperaCarregar,
    esperaDescarregar: aprendido?.esperaDescarregar ?? MODO_VIAGEM.esperaDescarregar,
    aprendido: Boolean(aprendido?.kmPorDia || aprendido?.esperaCarregar || aprendido?.esperaDescarregar),
    viagens: aprendido?.viagens ?? 0,
  };
}

// Monta o previsto.
//   trechos: [{ tipo: 'carregado' | 'vazio', de, para, km, frete_id?, valor?, pedagio? }]
//     (de/para: "Cidade/UF"; pedagio do trecho carregado: { modo, valor })
//   custo: custoDosNumeros() do Raio-X ({ porKm, pctDoFrete, consumo, diesel, despesas })
//   receita: o frete (com os complementos)
//   ritmo: ritmoDaEmpresa()
//   pedagio: o da viagem toda, quando a pessoa disse ({ modo: 'empresa' | 'contratante' | 'embutido', valor })
// Sem o custo (sem o consumo), devolve null.
export function montarPlano({ trechos, custo, receita, ritmo = ritmoDaEmpresa(null), pedagio = null, fonte, feitoEm, extras = {} }) {
  if (!custo?.porKm) return null;
  const partes = partesDasDespesas(custo);
  const km = { carregado: 0, vazio: 0, total: 0 };
  for (const t of trechos) {
    const n = Math.max(0, Math.round(Number(t.km) || 0));
    km[t.tipo === 'carregado' ? 'carregado' : 'vazio'] += n;
    km.total += n;
  }
  const cargas = trechos.filter((t) => t.tipo === 'carregado').length;
  // Pedágio: o que a pessoa disse para a viagem toda; senão, trecho a trecho
  // (o contratante paga: zero; já vem no frete: o valor do frete; senão, a
  // média por km da frota).
  let valorDoPedagio = 0;
  let fonteDoPedagio = 'media';
  if (pedagio?.modo === 'contratante') {
    fonteDoPedagio = 'contratante';
  } else if (ok(pedagio?.valor) && Number(pedagio.valor) >= 0) {
    valorDoPedagio = Number(pedagio.valor);
    fonteDoPedagio = pedagio.modo === 'embutido' ? 'embutido' : 'digitado';
  } else {
    for (const t of trechos) {
      if (t.tipo === 'carregado' && t.pedagio?.modo === 'contratante') continue;
      if (t.tipo === 'carregado' && t.pedagio?.modo === 'embutido' && ok(t.pedagio.valor)) {
        valorDoPedagio += Number(t.pedagio.valor);
        continue;
      }
      valorDoPedagio += partes.pedagio * (Number(t.km) || 0);
    }
  }
  const consumo = custo.consumo?.kmPorLitro ?? null;
  const preco = custo.diesel?.preco ?? null;
  const custos = {
    diesel: reais(custo.porKm.diesel * km.total),
    litros: consumo ? Math.round(km.total / consumo) : null,
    pedagio: reais(valorDoPedagio),
    porDia: reais(partes.porDia * km.total),
    outras: reais(partes.outras * km.total),
    reserva: reais(custo.porKm.reserva * km.total),
    motorista: reais((custo.pctDoFrete ?? 0) * (Number(receita) || 0)),
  };
  custos.total = reais(custos.diesel + custos.pedagio + custos.porDia + custos.outras + custos.reserva + custos.motorista);
  const dias = km.total / ritmo.kmPorDia + cargas * (ritmo.esperaCarregar + ritmo.esperaDescarregar);
  return {
    versao: 1,
    fonte,
    feito_em: feitoEm,
    numeros: {
      consumo,
      preco,
      porKm: { ...custo.porKm },
      partes,
      pct: custo.pctDoFrete ?? 0,
      kmPorDia: ritmo.kmPorDia,
      esperaCarregar: ritmo.esperaCarregar,
      esperaDescarregar: ritmo.esperaDescarregar,
      aprendido: ritmo.aprendido,
    },
    trechos: trechos.map((t) => ({
      tipo: t.tipo, de: t.de ?? null, para: t.para ?? null, km: Math.round(Number(t.km) || 0),
      ...(t.frete_id ? { frete_id: t.frete_id } : {}), ...(ok(t.valor) ? { valor: reais(t.valor) } : {}),
    })),
    km,
    receita: reais(receita),
    pedagio: { valor: custos.pedagio, fonte: fonteDoPedagio },
    custos,
    resultado: reais((Number(receita) || 0) - custos.total),
    dias: Math.round(dias * 10) / 10,
    ...extras,
  };
}

// O previsto de uma carga aceita no Raio-X: a ida carregada (com o desvio dos
// complementos) e, com a volta vazia, a mesma distância de volta. Dá a mesma
// sobra do Raio-X (com o pedágio pela média da frota).
//   r: raioX() · rota: { origem, uf_origem, destino, uf_destino }
export function planoDoRaioX({ r, custo, rota, voltaVazia, ritmo, pedagio = null, freteId = null, feitoEm }) {
  const de = lugarTexto(rota.origem, rota.uf_origem);
  const para = lugarTexto(rota.destino, rota.uf_destino);
  const trechos = [{ tipo: 'carregado', de, para, km: r.km + (r.kmExtra || 0), valor: r.total, frete_id: freteId }];
  if (voltaVazia) trechos.push({ tipo: 'vazio', de: para, para: de, km: r.km });
  return montarPlano({ trechos, custo, receita: r.total, ritmo, pedagio, fonte: 'raiox', feitoEm, extras: { voltaVazia: Boolean(voltaVazia) } });
}

// Os trechos de uma viagem pelas cargas dela: da base (ou de onde o caminhão
// está) até a primeira carga, cada carga, de uma carga para a outra e, no
// fim, de volta à base. Os complementos vão junto da carga principal.
//   cargas: as da viagem · base: { cidade, uf } · kmEntreCidades(de, para): km
//   pela estrada (ou null)
export function trechosDasCargas(cargas, { base, inicio = null, kmEntreCidades }) {
  const principais = (cargas ?? []).filter((f) => vivo(f) && !f.complemento_de)
    .sort((a, b) => String(a.data_carregamento).localeCompare(String(b.data_carregamento)));
  const complementos = (cargas ?? []).filter((f) => vivo(f) && f.complemento_de);
  const trechos = [];
  let onde = inicio ?? base;
  const igual = (a, b) => a && b && String(a.cidade ?? '').toUpperCase() === String(b.cidade ?? '').toUpperCase() && a.uf === b.uf;
  for (const f of principais) {
    const origem = { cidade: f.origem_cidade, uf: f.origem_uf };
    const destino = { cidade: f.destino_cidade, uf: f.destino_uf };
    if (onde && origem.cidade && !igual(onde, origem)) {
      trechos.push({ tipo: 'vazio', de: lugarTexto(onde.cidade, onde.uf), para: lugarTexto(origem.cidade, origem.uf), km: kmEntreCidades(onde, origem) ?? 0 });
    }
    const dosComplementos = complementos.filter((c) => c.complemento_de === f.id);
    const valor = [f, ...dosComplementos].reduce((s, x) => s + centavos(x.valor_frete), 0) / 100;
    trechos.push({
      tipo: 'carregado',
      de: lugarTexto(origem.cidade, origem.uf),
      para: lugarTexto(destino.cidade, destino.uf),
      km: ok(f.km_estimado) && Number(f.km_estimado) > 0 ? Number(f.km_estimado) : kmEntreCidades(origem, destino) ?? 0,
      valor,
      frete_id: f.id,
      pedagio: {
        modo: f.pedagio_modo === 'contratante_tag' ? 'contratante' : f.pedagio_modo === 'embutido' ? 'embutido' : 'empresa',
        valor: f.pedagio_modo === 'embutido' ? Number(f.pedagio_valor) || 0 : null,
      },
    });
    onde = destino.cidade ? destino : onde;
  }
  if (base && onde && !igual(onde, base) && principais.length > 0) {
    trechos.push({ tipo: 'vazio', de: lugarTexto(onde.cidade, onde.uf), para: lugarTexto(base.cidade, base.uf), km: kmEntreCidades(onde, base) ?? 0 });
  }
  return trechos;
}

// O previsto pelas cargas (quando a viagem não veio do Raio-X).
export function planoDasCargas({ cargas, custo, ritmo, base, inicio = null, kmEntreCidades, feitoEm }) {
  const trechos = trechosDasCargas(cargas, { base, inicio, kmEntreCidades });
  if (!trechos.some((t) => t.tipo === 'carregado')) return null;
  const receita = trechos.reduce((s, t) => s + (Number(t.valor) || 0), 0);
  return montarPlano({ trechos, custo, receita, ritmo, fonte: 'cargas', feitoEm });
}

// Os km pela estrada entre duas cidades com coordenadas (linha reta + 25%).
export function kmPelaEstrada(a, b) {
  if (!ok(a?.lat) || !ok(b?.lat)) return null;
  const reta = kmEntre(a, b);
  return reta > 0 ? Math.max(10, Math.round((reta * FATOR_DA_ESTRADA) / 10) * 10) : 0;
}

// ---------------------------------------------------------------------
// Horários
// ---------------------------------------------------------------------
// Quando o caminhão deve chegar a cada lugar do previsto, a partir do início.
// [{ lugar, chegada (ISO), motivo }]
export function horariosPrevistos(plano, { inicio }) {
  if (!plano || !inicio) return [];
  const n = plano.numeros;
  let t = Date.parse(inicio);
  const saida = [];
  for (const trecho of plano.trechos) {
    if (trecho.tipo === 'carregado') t += n.esperaCarregar * DIA_MS;
    t += (trecho.km / n.kmPorDia) * DIA_MS;
    saida.push({ lugar: trecho.para, chegada: new Date(t).toISOString(), motivo: trecho.tipo === 'carregado' ? 'descarregar' : 'vazio', frete_id: trecho.frete_id ?? null });
    if (trecho.tipo === 'carregado') t += n.esperaDescarregar * DIA_MS;
  }
  return saida;
}

// As regras de descanso do motorista profissional (CTB, art. 67-C).
export const REGRAS_DE_DESCANSO = [
  'No máximo 5h30 dirigindo sem parar.',
  '30 minutos de descanso a cada 6 horas dirigindo (dá para dividir).',
  '11 horas de descanso a cada 24 horas (as primeiras 8 seguidas).',
];

// ---------------------------------------------------------------------
// Abastecimento planejado
// ---------------------------------------------------------------------
// Onde abastecer: a cada "autonomia" (o tanque menos a reserva, vezes o
// consumo), saindo de tanque cheio. O ponto é aproximado (a linha entre as
// cidades da viagem) e ganha o nome da cidade-polo mais perto.
//   trechos: os do previsto, com de/para em coordenadas ({ lat, lon })
//   polos: [{ nome, uf, lat, lon }]
export function abastecimentosPlanejados({ trechos, consumo, tanque, preco, polos = [] }) {
  if (!(Number(tanque) > 0) || !(Number(consumo) > 0) || !Array.isArray(trechos)) return [];
  const litros = Math.round(Number(tanque) * (1 - MODO_VIAGEM.reservaDoTanque));
  const autonomia = litros * Number(consumo);
  const total = trechos.reduce((s, t) => s + (Number(t.km) || 0), 0);
  const pontos = [];
  for (let marco = autonomia; marco < total; marco += autonomia) {
    let antes = 0;
    let ponto = null;
    for (const t of trechos) {
      const km = Number(t.km) || 0;
      if (marco <= antes + km && km > 0) {
        const f = (marco - antes) / km;
        if (ok(t.de?.lat) && ok(t.para?.lat)) {
          ponto = { lat: t.de.lat + (t.para.lat - t.de.lat) * f, lon: t.de.lon + (t.para.lon - t.de.lon) * f };
        }
        break;
      }
      antes += km;
    }
    let pertoDe = null;
    if (ponto) {
      let melhor = Infinity;
      for (const p of polos) {
        const d = kmEntre(ponto, p);
        if (d < melhor && d <= MODO_VIAGEM.pertoDoPolo) {
          melhor = d;
          pertoDe = lugarTexto(p.nome, p.uf);
        }
      }
    }
    pontos.push({ km: Math.round(marco / 10) * 10, perto_de: pertoDe, litros, valor: ok(preco) ? reais(litros * Number(preco)) : null });
  }
  return pontos;
}

// ---------------------------------------------------------------------
// Manutenção e documentos
// ---------------------------------------------------------------------
// Os itens do plano de manutenção que vencem antes de a viagem acabar.
//   plano: planoDoVeiculo() de lib/manutencao.js · kmFaltando e diasFaltando:
//   o que ainda falta da viagem
export function manutencaoNaViagem(planoDoVeiculo, { kmFaltando = 0, diasFaltando = 0 }) {
  return (planoDoVeiculo ?? []).filter(({ situacao: s }) => s.estado === 'vencida'
    || (s.faltaKm !== null && s.faltaKm <= kmFaltando)
    || (s.faltaDias !== null && s.faltaDias <= Math.ceil(diasFaltando)));
}

export const DOCUMENTOS_DA_CARGA = [
  ['cte', 'CT-e'], ['mdfe', 'MDF-e (manifesto)'], ['nfe', 'Nota fiscal (DANFE)'], ['ciot', 'CIOT'],
  ['vale_pedagio', 'Vale-pedágio'], ['canhoto', 'Canhoto assinado'],
];
export const nomeDoDocumento = (codigo) => DOCUMENTOS_DA_CARGA.find(([c]) => c === codigo)?.[1] ?? 'Documento';

// Os documentos de uma carga: [{ codigo, nome, fotos, ok }]. O vale-pedágio
// só quando o contratante paga o pedágio; o canhoto, depois da entrega (ou
// quando o comprovante é pedido).
export function documentosDaCarga(frete, anexos = []) {
  const fotos = (anexos ?? []).filter((a) => vivo(a) && a.frete_id === frete.id && a.documento);
  const entregue = Boolean(frete.entregue_em || frete.data_descarga);
  return DOCUMENTOS_DA_CARGA
    .filter(([c]) => (c !== 'vale_pedagio' || frete.pedagio_modo === 'contratante_tag')
      && (c !== 'canhoto' || entregue || (frete.comprovante_status && frete.comprovante_status !== 'nao_precisa')))
    .map(([codigo, nome]) => {
      const dele = fotos.filter((a) => a.documento === codigo);
      const comNumero = codigo === 'cte' && Boolean(frete.cte_numero);
      const comprovante = codigo === 'canhoto' && ['postado', 'entregue'].includes(frete.comprovante_status);
      return { codigo, nome, fotos: dele, ok: dele.length > 0 || comNumero || comprovante };
    });
}

// Os documentos do motorista e do caminhão que vencem até o fim da viagem
// (mais o aviso de 30 dias). [{ nome, data, vencido }]
export function vencimentos({ motorista = null, caminhao = null, hoje, ate = null }) {
  const limite = ate ?? hoje;
  const lista = [
    motorista?.cnh_validade && { nome: 'CNH do motorista', data: motorista.cnh_validade },
    motorista?.toxicologico_ate && { nome: 'Exame toxicológico', data: motorista.toxicologico_ate },
    caminhao?.licenciamento_ate && { nome: 'Licenciamento (CRLV)', data: caminhao.licenciamento_ate },
    caminhao?.seguro_ate && { nome: 'Seguro do caminhão', data: caminhao.seguro_ate },
  ].filter(Boolean);
  return lista
    .filter((d) => diasEntre(limite, d.data) <= MODO_VIAGEM.avisoDeDocumento)
    .map((d) => ({ ...d, vencido: d.data < hoje, venceNaViagem: d.data >= hoje && d.data <= limite }))
    .sort((a, b) => a.data.localeCompare(b.data));
}

// ---------------------------------------------------------------------
// O realizado
// ---------------------------------------------------------------------
const DO_PEDAGIO = new Set(['pedagio', 'tag_pedagio']);
const DO_DIA = new Set(['alimentacao', 'pernoite', 'estacionamento']);

// Onde entra cada despesa da viagem: diesel, pedagio, porDia, outras (o grupo
// "viagem") ou fora (manutenção, multa, fixos: não entram na conta do
// previsto, que usa a reserva por km).
export function classeDaDespesa(l, categoriaPorId) {
  const cat = categoriaPorId.get(l.categoria_id);
  if (!cat) return 'fora';
  if (cat.exige_hodometro) return 'diesel';
  if (cat.grupo !== 'viagem') return 'fora';
  if (DO_PEDAGIO.has(cat.codigo)) return 'pedagio';
  if (DO_DIA.has(cat.codigo)) return 'porDia';
  return 'outras';
}

// Os km rodados de verdade: o da chegada menos o da saída (na viagem ou nos
// eventos); sem isso, as leituras do hodômetro nas despesas da viagem.
export function kmRodados({ viagem, eventos, despesas = [] }) {
  if (ok(viagem?.km_chegada) && ok(viagem?.km_saida) && viagem.km_chegada > viagem.km_saida) {
    return { km: viagem.km_chegada - viagem.km_saida, fonte: 'viagem' };
  }
  const inicio = inicioDaViagem(eventos);
  const fim = fimDaViagem(eventos);
  const kmInicio = ok(inicio?.km) ? Number(inicio.km) : ok(viagem?.km_saida) ? Number(viagem.km_saida) : null;
  const kmFim = ok(fim?.km) ? Number(fim.km) : ok(viagem?.km_chegada) ? Number(viagem.km_chegada) : null;
  if (kmInicio !== null && kmFim !== null && kmFim > kmInicio) return { km: kmFim - kmInicio, fonte: 'eventos' };
  const leituras = despesas.filter((l) => vivo(l) && ok(l.hodometro)).map((l) => Number(l.hodometro));
  if (kmInicio !== null) leituras.push(kmInicio);
  if (leituras.length >= 2) {
    const km = Math.max(...leituras) - Math.min(...leituras);
    if (km > 0) return { km, fonte: 'leituras', parcial: true };
  }
  return { km: null, fonte: null };
}

// Os dias da viagem: do "Iniciar" ao "Encerrar" (com a hora); sem eles, pelas
// datas de saída e chegada.
export function diasDaViagem({ viagem, eventos, agora = null }) {
  const inicio = inicioDaViagem(eventos);
  const fim = fimDaViagem(eventos);
  if (inicio && (fim || agora)) {
    const ate = fim ? fim.momento : agora;
    return { dias: Math.max(0, (Date.parse(ate) - Date.parse(inicio.momento)) / DIA_MS), fonte: 'horarios', emAndamento: !fim };
  }
  if (viagem?.data_saida && viagem?.data_chegada) {
    return { dias: diasEntre(viagem.data_saida, viagem.data_chegada) + 1, fonte: 'datas', emAndamento: false };
  }
  return { dias: null, fonte: null, emAndamento: true };
}

// Quanto esperou para carregar e descarregar (dias), pelas chegadas e saídas.
export function esperasDaViagem(eventos, cargas = []) {
  const soma = { carregar: 0, descarregar: 0, vezes: 0 };
  const fretes = new Set(eventosEmOrdem(eventos).filter((e) => e.frete_id).map((e) => e.frete_id));
  const porId = new Map((cargas ?? []).map((f) => [f.id, f]));
  for (const id of fretes) {
    for (const motivo of ['carregar', 'descarregar']) {
      const p = passagem(eventos, id, motivo, porId.get(id));
      if (p.chegada && p.saida && p.saida.momento > p.chegada.momento) {
        soma[motivo] += (Date.parse(p.saida.momento) - Date.parse(p.chegada.momento)) / DIA_MS;
        soma.vezes += 1;
      }
    }
  }
  return soma;
}

// O realizado da viagem, nas mesmas partes do previsto.
//   cargas, despesas: as da viagem · categorias: todas · motorista: o que o
//   motorista ganhou nela (comissão, diárias…) e o agenciamento das cargas ·
//   pedagioNaTag: o pedágio pago na tag nesta viagem, quando a pessoa disse
//   (a fatura da tag é lançada no mês)
export function realizadoDaViagem({ viagem, cargas, despesas, categorias, eventos, motorista = 0, plano = null, agora = null }) {
  const categoriaPorId = new Map((categorias ?? []).map((c) => [c.id, c]));
  const soma = { diesel: 0, pedagio: 0, porDia: 0, outras: 0, fora: 0 };
  let litros = 0;
  for (const l of despesas ?? []) {
    if (!vivo(l) || l.tipo !== 'despesa' || l.viagem_id !== viagem.id) continue;
    const classe = classeDaDespesa(l, categoriaPorId);
    soma[classe] += centavos(l.valor);
    if (classe === 'diesel' && Number(l.litros) > 0) litros += Number(l.litros);
  }
  const fim = fimDaViagem(eventos);
  const naTag = ok(fim?.dados?.pedagio_tag) ? centavos(fim.dados.pedagio_tag) : 0;
  const daViagem = (cargas ?? []).filter((f) => vivo(f) && f.viagem_id === viagem.id);
  const receita = daViagem.reduce((s, f) => s + centavos(f.valor_frete) + centavos(f.estadia_valor), 0) / 100;
  const km = kmRodados({ viagem, eventos, despesas: (despesas ?? []).filter((l) => l.viagem_id === viagem.id) });
  const dias = diasDaViagem({ viagem, eventos, agora });
  const reservaKm = Number(plano?.numeros?.porKm?.reserva) || 0;
  const custos = {
    diesel: soma.diesel / 100,
    litros: litros > 0 ? Math.round(litros) : null,
    pedagio: (soma.pedagio + naTag) / 100,
    porDia: soma.porDia / 100,
    outras: soma.outras / 100,
    reserva: km.km !== null ? reais(reservaKm * km.km) : reais(plano?.custos?.reserva ?? 0),
    motorista: reais(motorista),
  };
  custos.total = reais(custos.diesel + custos.pedagio + custos.porDia + custos.outras + custos.reserva + custos.motorista);
  return {
    receita: reais(receita),
    cargas: daViagem.filter((f) => !f.complemento_de).length,
    custos,
    fora: soma.fora / 100,
    pedagioNaTag: naTag / 100,
    km,
    dias,
    esperas: esperasDaViagem(eventos, daViagem),
    resultado: reais(receita - custos.total),
  };
}

// O que o motorista ganhou na viagem (os créditos já lançados e os que o
// acerto ainda vai lançar: comissão, diárias…) e o agenciamento das cargas.
//   creditosDaViagem: o de lib/acerto.js
export function motoristaDaViagem({ viagem, cargas, regras, lancamentos, creditosDaViagem }) {
  const daViagem = (cargas ?? []).filter((f) => vivo(f) && f.viagem_id === viagem.id);
  const lancados = (lancamentos ?? []).filter((l) => vivo(l) && l.tipo === 'credito' && l.viagem_id === viagem.id
    && l.colaborador_id === viagem.motorista_id && l.subtipo !== 'agenciamento');
  const aLancar = creditosDaViagem ? creditosDaViagem(viagem, daViagem, regras ?? [], { jaLancados: lancamentos ?? [] }) : [];
  const total = lancados.reduce((s, l) => s + centavos(l.valor), 0)
    + aLancar.reduce((s, l) => s + centavos(l.valor), 0)
    + daViagem.reduce((s, f) => s + centavos(f.agenciamento_valor), 0);
  return total / 100;
}

// ---------------------------------------------------------------------
// O previsto e o realizado
// ---------------------------------------------------------------------
// As causas da diferença, cada uma com o efeito na sobra (negativo: piorou).
// [{ chave, nome, efeito, detalhe }], as maiores primeiro.
export function compararComPrevisto(plano, real) {
  if (!plano || !real) return null;
  const p = plano.custos;
  const r = real.custos;
  const causas = [];
  const juntar = (chave, nome, efeito, detalhe = null) => {
    const e = reais(efeito);
    if (Math.abs(e) >= MODO_VIAGEM.causaMinima) causas.push({ chave, nome, efeito: e, detalhe });
  };

  // Frete
  const maisCargas = real.cargas - plano.trechos.filter((t) => t.tipo === 'carregado').length;
  juntar('frete', 'Frete', real.receita - plano.receita,
    maisCargas > 0 ? `${maisCargas === 1 ? 'uma carga' : `${maisCargas} cargas`} a mais que o previsto` : null);

  // Combustível: os km, o consumo e o preço
  const detalhes = [];
  const kmPrevisto = plano.km.total;
  if (real.km.km !== null && Math.abs(real.km.km - kmPrevisto) >= 10) {
    detalhes.push(real.km.km > kmPrevisto ? `rodou ${Math.round(real.km.km - kmPrevisto)} km a mais` : `rodou ${Math.round(kmPrevisto - real.km.km)} km a menos`);
  }
  if (real.km.km !== null && r.litros > 0 && plano.numeros.consumo) {
    const consumo = real.km.km / r.litros;
    if (Math.abs(consumo - plano.numeros.consumo) >= 0.05) detalhes.push(`consumo de ${consumo.toFixed(2).replace('.', ',')} km/l (previsto ${Number(plano.numeros.consumo).toFixed(2).replace('.', ',')})`);
  }
  if (r.litros > 0 && plano.numeros.preco) {
    const preco = r.diesel / r.litros;
    if (Math.abs(preco - plano.numeros.preco) >= 0.05) detalhes.push(`diesel a R$ ${preco.toFixed(2).replace('.', ',')} o litro (previsto R$ ${Number(plano.numeros.preco).toFixed(2).replace('.', ',')})`);
  }
  juntar('combustivel', 'Combustível', p.diesel - r.diesel, detalhes.join('; ') || null);

  // Pedágio
  juntar('pedagio', 'Pedágio', p.pedagio - r.pedagio, plano.pedagio?.fonte === 'contratante' && r.pedagio > 0 ? 'o previsto era o contratante pagar' : null);

  // Espera e dias a mais (alimentação, pernoite e estacionamento)
  const diasReais = real.dias.dias;
  const partesDaEspera = [];
  if (diasReais !== null && diasReais >= MODO_VIAGEM.diasMinimos && Math.abs(diasReais - plano.dias) >= 0.3) {
    partesDaEspera.push(`a viagem levou ${diasReais.toFixed(1).replace('.', ',')} dias (previsto ${Number(plano.dias).toFixed(1).replace('.', ',')})`);
  }
  const esperou = real.esperas.carregar + real.esperas.descarregar;
  if (real.esperas.vezes > 0) partesDaEspera.push(`esperou ${textoDeHoras(esperou * 24)} para carregar e descarregar`);
  juntar('espera', 'Espera e dias a mais', p.porDia - r.porDia, partesDaEspera.join('; ') || null);

  juntar('outras', 'Outras despesas da viagem', p.outras - r.outras, 'chapa, balança, Arla e outras');
  juntar('motorista', 'Motorista e agenciamento', p.motorista - r.motorista, null);
  juntar('reserva', 'Reserva de manutenção', p.reserva - r.reserva, real.km.km !== null ? 'pelos km rodados' : null);

  causas.sort((a, b) => Math.abs(b.efeito) - Math.abs(a.efeito));
  const diferenca = reais(real.resultado - plano.resultado);
  return {
    previsto: plano.resultado,
    realizado: real.resultado,
    diferenca,
    causas,
    principais: causas.filter((c) => c.efeito < 0 || diferenca >= 0).slice(0, MODO_VIAGEM.principais),
    lucroPorDia: {
      previsto: plano.dias > 0 ? reais(plano.resultado / plano.dias) : null,
      realizado: diasReais >= MODO_VIAGEM.diasMinimos ? reais(real.resultado / diasReais) : null,
    },
  };
}

// "+R$ 117,00 (gastou mais)" · "−R$ 200,00 (recebeu menos)": o frete pelo que
// recebeu; os custos pelo que gastou.
export function sinalDaCausa(c) {
  if (c.chave === 'frete') return { mais: c.efeito > 0, valor: Math.abs(c.efeito), texto: c.efeito > 0 ? 'recebeu mais' : 'recebeu menos' };
  return { mais: c.efeito < 0, valor: Math.abs(c.efeito), texto: c.efeito < 0 ? 'gastou mais' : 'gastou menos' };
}

export function textoDeHoras(horas) {
  const h = Math.max(0, Math.round(Number(horas) || 0));
  if (h < 24) return `${h} ${h === 1 ? 'hora' : 'horas'}`;
  const dias = Math.round((h / 24) * 10) / 10;
  return `${String(dias).replace('.', ',')} ${dias === 1 ? 'dia' : 'dias'}`;
}

// ---------------------------------------------------------------------
// O que o app aprende com as viagens
// ---------------------------------------------------------------------
// Das viagens encerradas no último ano com o "Iniciar" e o "Encerrar": o
// ritmo (km por dia rodando, fora a espera para carregar e descarregar) e as
// esperas (a mediana). Só com viagens suficientes; senão, null em cada um.
//   tabelas: { viagens, eventos, lancamentos } · hoje: AAAA-MM-DD
export function aprendidoDasViagens(tabelas, { hoje }) {
  const viagens = (tabelas.viagens ?? []).filter((v) => vivo(v) && ['encerrada', 'acertada'].includes(v.status)
    && v.data_chegada && diasEntre(v.data_chegada, hoje) <= MODO_VIAGEM.diasParaAprender);
  const porViagem = new Map();
  for (const e of tabelas.eventos ?? []) {
    if (!vivo(e)) continue;
    if (!porViagem.has(e.viagem_id)) porViagem.set(e.viagem_id, []);
    porViagem.get(e.viagem_id).push(e);
  }
  const ritmos = [];
  const carregar = [];
  const descarregar = [];
  const cargaPorId = new Map((tabelas.fretes ?? []).map((f) => [f.id, f]));
  for (const v of viagens) {
    const eventos = porViagem.get(v.id) ?? [];
    const inicio = inicioDaViagem(eventos);
    const fim = fimDaViagem(eventos);
    const cargas = [];
    for (const id of new Set(eventos.filter((e) => e.frete_id).map((e) => e.frete_id))) {
      if (cargaPorId.has(id)) cargas.push(cargaPorId.get(id));
      for (const [motivo, lista] of [['carregar', carregar], ['descarregar', descarregar]]) {
        const p = passagem(eventos, id, motivo, cargaPorId.get(id));
        if (p.chegada && p.saida && p.saida.momento > p.chegada.momento) lista.push((Date.parse(p.saida.momento) - Date.parse(p.chegada.momento)) / DIA_MS);
      }
    }
    if (!inicio || !fim || fim.dados?.aproximado || inicio.dados?.aproximado) continue;
    const despesas = (tabelas.lancamentos ?? []).filter((l) => l.viagem_id === v.id);
    const km = kmRodados({ viagem: v, eventos, despesas });
    const total = (Date.parse(fim.momento) - Date.parse(inicio.momento)) / DIA_MS;
    const esperas = esperasDaViagem(eventos, cargas);
    const rodando = total - esperas.carregar - esperas.descarregar;
    if (km.km && !km.parcial && rodando > 0.1) ritmos.push(km.km / rodando);
  }
  const kmPorDia = ritmos.length >= MODO_VIAGEM.viagensParaAprender
    ? Math.round(limitar(mediana(ritmos), MODO_VIAGEM.kmPorDiaAprendido.min, MODO_VIAGEM.kmPorDiaAprendido.max) / 10) * 10
    : null;
  const espera = (lista) => (lista.length >= MODO_VIAGEM.esperasParaAprender
    ? Math.round(limitar(mediana(lista), MODO_VIAGEM.esperaAprendida.min, MODO_VIAGEM.esperaAprendida.max) * 100) / 100
    : null);
  return {
    viagens: ritmos.length,
    kmPorDia,
    esperaCarregar: espera(carregar),
    esperaDescarregar: espera(descarregar),
  };
}
